import { Fragment, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { isEmpty } from 'ramda';
import { connect, useDispatch } from 'react-redux';

import Printer from '@gef-ui/components/atoms/SvgIcon/Icons/Printer';
import Form from '@gef-ui/form-material/Form';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import { Autorenew } from 'components/atoms/CustomSVGIcon';
import Sidebar from 'components/atoms/GefMui/Sidebar/Sidebar';
import { USER_ROLES } from 'constants/general';
import fetch from 'features/common/fetch';
import campaignDetail from 'features/views/campaignManagement/campaignDetail';
import contactReleasing from 'features/views/campaignManagement/contactReleasing';
import {
	automaticAssignSubProcesses,
	printModeSubProcesses,
	processes,
} from 'features/views/campaignManagement/contactReleasing/constants';
import contacts from 'features/views/campaignManagement/contacts';
import formDescriptorFn, {
	CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME,
	FIELDS,
} from 'features/views/campaignManagement/sidebar/form';
import user from 'features/views/user';

import SidebarAutoComplete from './SidebarAutoComplete';
import SidebarContactReleaseNew from './SidebarContactReleaseNew';
import SidebarManualAssignNew from './SidebarManualAssignNew';

const { urls } = fetch.constants;

const notAllowedRoles = [USER_ROLES.SELLER, USER_ROLES.PPZ];

const CampaignSidebar = ({
	campaignId,
	execute,
	executeAll,
	resetSelectedContacts,
	currentRole,
	allowSetPrint,
	isProcessRunning,
	campaignDetail,
}) => {
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(contactReleasing.actions.startupCampaignManagementSidebar());
	}, [dispatch]);

	const handleExecuteAll = useCallback(() => {
		executeAll();
	}, [executeAll]);

	const handleExecute = useCallback(() => {
		execute();
	}, [execute]);

	const handleCancel = useCallback(() => {
		resetSelectedContacts();
	}, [resetSelectedContacts]);

	const { channelId = null, nominationDate = false } = campaignDetail;

	const printModeOptions = useMemo(
		() => [
			{
				id: printModeSubProcesses.NO_PRINT,
				shortDescription: getLabelMessage('campaignManagement:noPrint'),
			},
			{
				id: printModeSubProcesses.ANONYMOUS,
				shortDescription: getLabelMessage('campaignManagement:anonymousPrint'),
			},
			{
				id: printModeSubProcesses.NAMED,
				shortDescription: getLabelMessage('campaignManagement:namedPrint'),
			},
		],
		[],
	);

	const automaticAssignOptions = useMemo(
		() => [
			{
				id: automaticAssignSubProcesses.ZIP_CODE,
				shortDescription: getLabelMessage('campaignManagement:assignByZipCode'),
			},
			{
				id: automaticAssignSubProcesses.PORTFOLIO,
				shortDescription: getLabelMessage('campaignManagement:assignBySolver'),
			},
		],
		[],
	);

	return (
		<Sidebar title={getLabelMessage('campaignManagement:assignReleaseContacts')}>
			{!isEmpty(campaignDetail) && (
				<Fragment>
					<Sidebar.Body>
						<Form name={CAMPAIGN_MANAGEMENT_SIDEBAR_FORM_NAME} formDescriptorFn={formDescriptorFn}>
							<Stack
								spacing={1.5}
								sx={{
									mx: 0.5,
									mt: 1,
								}}
							>
								{!notAllowedRoles.includes(currentRole) && <SidebarContactReleaseNew />}
								{!notAllowedRoles.includes(currentRole) && (
									<Tooltip
										title={getLabelMessage('tooltips:printNotAllowed')}
										placement="left"
										disableHoverListener={allowSetPrint}
										disableFocusListener={allowSetPrint}
										disableTouchListener={allowSetPrint}
									>
										<Stack>
											<SidebarAutoComplete
												fieldName={FIELDS.printMode}
												process={processes.PRINT_MODE}
												label={getLabelMessage('campaignManagement:printingType')}
												options={printModeOptions}
												icon={<Printer />}
												disabled={!allowSetPrint}
											/>
										</Stack>
									</Tooltip>
								)}
								{[USER_ROLES.CENTRAL, USER_ROLES.AGENCY_MANAGER].includes(currentRole) && (
									<SidebarAutoComplete
										fieldName={FIELDS.automaticAssign}
										process={processes.AUTOMATIC_ASSIGN}
										label={getLabelMessage('campaignManagement:automaticAssign')}
										options={automaticAssignOptions}
										icon={<Autorenew />}
										disabled={nominationDate}
									/>
								)}
								<SidebarManualAssignNew
									campaignId={campaignId}
									campaignChannel={channelId}
									isNominatedCampaign={nominationDate}
								/>
							</Stack>
						</Form>
					</Sidebar.Body>
					<Sidebar.Footer>
						<ComponentLoader fetchStorePath={urls.contactReleaseContact} ignoreErrors>
							<ComponentLoader fetchStorePath={urls.contactSetPrintType} ignoreErrors>
								<ComponentLoader fetchStorePath={urls.contactAutoAssignContactList} ignoreErrors>
									<ComponentLoader fetchStorePath={urls.contactManualAssignContactList} ignoreErrors>
										<Button onClick={handleExecute} disabled={isProcessRunning} fullWidth>
											{getLabelMessage('campaignManagement:execute')}
										</Button>
									</ComponentLoader>
								</ComponentLoader>
							</ComponentLoader>
						</ComponentLoader>
						<ComponentLoader fetchStorePath={urls.contactReleaseAll} ignoreErrors>
							<ComponentLoader fetchStorePath={urls.contactSetPrintAll} ignoreErrors>
								<ComponentLoader fetchStorePath={urls.contactAssignAll} ignoreErrors>
									<Button onClick={handleExecuteAll} disabled={isProcessRunning} fullWidth>
										{getLabelMessage('campaignManagement:executeAll')}
									</Button>
								</ComponentLoader>
							</ComponentLoader>
						</ComponentLoader>
						<Button onClick={handleCancel} fullWidth>
							{getLabelMessage('campaignManagement:cancel')}
						</Button>
					</Sidebar.Footer>
				</Fragment>
			)}
		</Sidebar>
	);
};

CampaignSidebar.propTypes = {
	allowSetPrint: PropTypes.bool,
	campaignDetail: PropTypes.object,
	campaignId: PropTypes.number,
	currentRole: PropTypes.string,
	execute: PropTypes.func,
	executeAll: PropTypes.func,
	isProcessRunning: PropTypes.bool,
	resetSelectedContacts: PropTypes.func,
};

export default connect(
	(state) => {
		return {
			campaignDetail: campaignDetail.selectors.getCampaignDetail(state),
			isProcessRunning: contactReleasing.selectors.getIsProcessRunning(state),
			allowSetPrint: campaignDetail.selectors.getAllowSetPrint(state),
			currentRole: user.selectors.getCurrentRole(state),
		};
	},
	{
		execute: contactReleasing.actions.execute,
		executeAll: contactReleasing.actions.executeAll,
		resetSelectedContacts: contacts.actions.resetSelectedContacts,
	},
)(CampaignSidebar);
